import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Lock, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CommentsTab } from './CommentsTab';
import { MessagesTab } from './MessagesTab';
import { PendingInboxTab } from './PendingInboxTab';
import { MetaAccountsDialog } from './MetaAccountsDialog';
import { InboxAccessDialog } from './InboxAccessDialog';
import { MetaPermissionsDialog } from './MetaPermissionsDialog';
import { useAuthStore } from '../store';
import { metaSocialAPI } from '../lib/api';
import { useInboxAccess } from '../hooks/useInboxAccess';
import { META_DIRECT_ENABLED } from '../lib/features';

export default function SocialInboxPage() {
  const { user } = useAuthStore();
  const isCeo = user?.role === 'ceo';
  const allowed = useInboxAccess(user);
  const [status, setStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const loadClients = useCallback(() => metaSocialAPI.enabledClients().then(setClients).catch(() => setClients([])), []);
  useEffect(() => { if (allowed) { metaSocialAPI.status().then(setStatus).catch(() => {}); loadClients(); } }, [loadClients, allowed]);

  if (allowed === null) return null;
  if (!META_DIRECT_ENABLED) {
    return <p className="text-sm text-muted-foreground py-16 text-center" data-testid="social-inbox-parked">La bandeja social está aparcada: comentarios y mensajes se gestionan en Metricool.</p>;
  }
  if (!allowed) {
    return <p className="text-sm text-muted-foreground py-16 text-center flex flex-col items-center gap-2" data-testid="social-inbox-no-access"><Lock className="w-6 h-6" />No tienes acceso a la bandeja social. Pídeselo a una CEO.</p>;
  }
  return (
    <div className="space-y-5" data-testid="social-inbox-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold flex items-center gap-2"><MessageSquare className="w-7 h-7 text-primary" />Bandeja social</h1>
        {isCeo && <div className="flex gap-2"><MetaPermissionsDialog /><InboxAccessDialog /><MetaAccountsDialog onSaved={loadClients} /></div>}
      </div>
      {status && (!status.ok || status.warning) && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="meta-token-alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{status.message}
        </div>
      )}
      {!clients.length ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="social-inbox-empty">Todavía no hay clientes con la publicación directa activada{isCeo ? ': actívala en «Cuentas».' : '.'}</p>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="inbox-tab-pending">Pendientes</TabsTrigger>
            <TabsTrigger value="comments" data-testid="inbox-tab-comments">Historial</TabsTrigger>
            <TabsTrigger value="messages" data-testid="inbox-tab-messages">Mensajes</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-4"><PendingInboxTab isCeo={isCeo} /></TabsContent>
          <TabsContent value="comments" className="mt-4"><CommentsTab clients={clients} /></TabsContent>
          <TabsContent value="messages" className="mt-4"><MessagesTab clients={clients} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
